/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import { Typography2 } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import type { EnterpriseContract } from 'bundles/enterprise-admin-account/constants/types';
import { enterpriseContractUtilizationType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import { CONTRACT_STATE_TYPES } from 'bundles/enterprise-admin-contracts/utils/AdminContractUtils';
import { FREEMIUM_INVITE_LEARNERS_PATH } from 'bundles/enterprise-admin-coursera-for-campus-basic/constants';
import CustomizeAnalyticsSettingsButton from 'bundles/enterprise-admin-home/components/CustomizeAnalyticsSettingsButton';
import LearnerEngagementHeaderActions from 'bundles/enterprise-admin-home/components/LearnerEngagementHeaderActions';
import UsageOverview, {
  mapUsageSummaryToUsageOverviewProps,
} from 'bundles/enterprise-admin-home/components/UsageOverview';
import { disableNonScalableOrgActionsOrgs } from 'bundles/enterprise-admin-home/constants';
import type { ProgramNameList } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import type { PresetId } from 'bundles/enterprise-admin-messages/types';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { Spacer } from 'bundles/enterprise-ui/components';
import type { EnterpriseOrganizationSummary } from 'bundles/naptimejs/resources/EnterpriseOrganizationSummaries.v1';

import _t from 'i18n!nls/enterprise-admin-home';

import 'css!./__styles__/ThirdPartyOrgUsageOverview';

const { TERMINATED, EXPIRED } = CONTRACT_STATE_TYPES;
const { NO_LIMIT, USER_LICENSE_WITH_ENROLLMENT_CAP } = enterpriseContractUtilizationType;

export type SendMessageParams = {
  presetId: PresetId;
  numberOfRecipients: number;
};

type PropsFromGetContext = {
  enableNewProgramCreation: boolean;
  enableNewLearnersPage: boolean;
};

type PropsFromCaller = {
  usageSummary?: EnterpriseOrganizationSummary;
  programNameList: ProgramNameList;
  thirdPartyOrganization: {
    id: string;
    slug: string;
  };
  sendMessage: (params: SendMessageParams) => void;
  hasAccessToAnalytics: boolean;
  enableSkillsPlanning?: boolean;
  className?: string;
  contracts: Array<{
    contractState?: EnterpriseContract['contractState'] | null;
    limitType?: EnterpriseContract['limitType'];
    startsAt?: EnterpriseContract['startsAt'];
  }>;
};

export type Props = PropsFromCaller & PropsFromGetContext;

export const ThirdPartyOrgUsageOverview = ({
  usageSummary,
  programNameList,
  thirdPartyOrganization,
  sendMessage,
  hasAccessToAnalytics,
  enableSkillsPlanning,
  className,
  enableNewProgramCreation,
  contracts,
  enableNewLearnersPage,
}: Props) => {
  const {
    invitationsSent,
    learnersJoined,
    enrolledLearners,
    remainingLicenses,
    notJoined,
    remainingEnrollments,
    notEnrolledLearners,
    invitationsFunnelSize,
    learnersJoinedFunnelSize,
    enrolledLearnersFunnelSize,
  } = mapUsageSummaryToUsageOverviewProps(usageSummary);
  const enableC4CBFreemium = enterpriseAdminUser.isC4CBOrg;
  const disableNonScalableOrgActions = disableNonScalableOrgActionsOrgs.includes(thirdPartyOrganization.id);
  const validContracts = contracts.filter(
    (contract) => contract.contractState !== TERMINATED && contract.contractState !== EXPIRED
  );
  const isLinkedToUnlimitedContract = validContracts.some((contract): boolean => contract.limitType === NO_LIMIT);

  const isOnlyLinkedToLicensesWithPoolContract = !validContracts.some(
    (contract) => contract.limitType !== USER_LICENSE_WITH_ENROLLMENT_CAP
  );

  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'learner_engagement',
      },
      ...enterpriseAdminTrackingData,
    },
    [],
    { fullyVisible: true }
  );

  const contractStartDates = validContracts
    .filter((_) => _ !== undefined)
    .map((_) => new Date(_?.startsAt ?? '').getTime());
  const contractStartDate = Math.min(...contractStartDates);
  const inviteLearnersLink = () => {
    if (enableC4CBFreemium) {
      return `/o/${thirdPartyOrganization.slug}/admin/programs/${FREEMIUM_INVITE_LEARNERS_PATH}`;
    } else if (enableNewProgramCreation) {
      return `/o/${thirdPartyOrganization.slug}/program-creation/choose-program`;
    } else {
      return `/o/${thirdPartyOrganization.slug}/admin/users?showImportLearnersModalOnMount=true`;
    }
  };

  return (
    <div
      ref={visibilityTrackingRef}
      className={classNames('rc-ThirdPartyOrgUsageOverview', className)}
      data-e2e="enterprise-usage-overview"
    >
      <Box justifyContent="between" alignItems="end">
        <Typography2 variant="titleSmall" component="h2">
          {_t('Learner engagement with the platform')}
          <CustomizeAnalyticsSettingsButton sectionName="learner_engagement" />
        </Typography2>
        <Spacer size={8} />
        {!enableSkillsPlanning && (
          <LearnerEngagementHeaderActions
            thirdPartyOrganization={thirdPartyOrganization}
            programNameList={programNameList}
          />
        )}
      </Box>
      <Spacer size={24} />
      <UsageOverview
        {...{
          invitationsSent,
          learnersJoined,
          enrolledLearners,
          remainingLicenses,
          notJoined,
          remainingEnrollments,
          notEnrolledLearners,
        }}
        hideRemainingLicenses={isLinkedToUnlimitedContract}
        hideRemainingEnrollments={isLinkedToUnlimitedContract || !isOnlyLinkedToLicensesWithPoolContract}
        thirdPartyOrganizationId={thirdPartyOrganization.id}
        manageLearnersLink={
          enableNewLearnersPage
            ? `/o/${thirdPartyOrganization.slug}/admin/learners`
            : `/o/${thirdPartyOrganization.slug}/admin/users`
        }
        funnelStages={[invitationsFunnelSize, learnersJoinedFunnelSize, enrolledLearnersFunnelSize]}
        enrollmentReportLink={
          hasAccessToAnalytics
            ? `/o/${thirdPartyOrganization.slug}/admin/analytics/enrollments?source=ADMIN_HOME`
            : undefined
        }
        licenceUsageReportLink={
          hasAccessToAnalytics
            ? `/o/${thirdPartyOrganization.slug}/admin/analytics/utilization?source=ADMIN_HOME`
            : undefined
        }
        inviteLearnersLink={inviteLearnersLink()}
        sendReminderToJoin={
          notJoined !== undefined
            ? () => sendMessage({ presetId: 'remind-join', numberOfRecipients: notJoined })
            : undefined
        }
        sendReminderToEnroll={
          !disableNonScalableOrgActions && notEnrolledLearners !== undefined
            ? () => {
                sendMessage({ presetId: 'remind-enroll', numberOfRecipients: notEnrolledLearners });
              }
            : undefined
        }
        contractStartDate={contractStartDate}
      />
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  getContext({
    enableNewProgramCreation: PropTypes.bool,
    enableNewLearnersPage: PropTypes.bool,
  })
)(ThirdPartyOrgUsageOverview);
